/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { action, observable, computed } from 'mobx';
import { ACTION_ID } from '../utils/modelMapper/appModelMapper/appModelConstants';
import TranslationUtil from '../utils/translation';
import { PRODUCT_CTA_PRIORITY } from '../utils/enums';

/**
 * Whether an app satisfies given filters
 * @param  {[type]} app
 * @param  {[type]} filters
 * @return {boolean}
 */
const matchFilters = (app, filters) => {
    let include = true;
    Object.keys(filters).forEach((filterKey) => {
        if (!Object.prototype.hasOwnProperty.call(filters, filterKey)) {
            return;
        }
        const appFilterValue = app[filterKey];
        if (appFilterValue) {
            if (typeof appFilterValue === 'string') {
                include = include && appFilterValue === filters[filterKey];
            } else if (typeof appFilterValue === 'boolean') {
                include = include && appFilterValue === filters[filterKey];
            } else if (Array.isArray(appFilterValue.slice())) {
                include = include && (
                    !filters[filterKey]
                    || appFilterValue.includes(filters[filterKey])
                );
            }
        }
    });
    return include;
};

const versionComp = (versionA = '', versionB = '') => {
    const versionAParts = versionA.split('.');
    const versionBParts = versionB.split('.');
    let it = 0;
    for (; it < Math.min(versionAParts.length, versionBParts.length); it += 1) {
        if (versionAParts[it] !== versionBParts[it]) {
            return (parseInt(versionAParts[it], 10) < parseInt(versionBParts[it], 10));
        }
    }
    return versionAParts.length < versionBParts.length;
};

class Apps {
    @observable apps = [];

    locale = 'en_US';

    @action
    addApp(app) {
        this.apps.push(app);
    }

    @action
    replaceApp(oldApp, newApp) {
        const existingAppIndex = this.apps.indexOf(oldApp);
        if (existingAppIndex === -1) {
            return;
        }
        this.apps.splice(existingAppIndex, 1, newApp);
    }

    @action
    addApps(apps) {
        this.apps = [...this.apps, ...apps];
    }

    @action
    clearAndAddApps(apps) {
        this.apps.replace(apps);
    }

    @action
    updateApps(apps) {
        apps.forEach((app) => {
            const filter = {
                sapcode: app.sapcode,
                codexVersion: app.codexVersion,
            };
            const filteredApps = this.filterApps(filter);
            filteredApps.forEach((fiterApp, index) => {
                if (index === 0) {
                    this.replaceApp(fiterApp, app);
                } else {
                    this.removeApp(fiterApp);
                }
            });
        });
    }

    @action
    removeApp(app) {
        return this.apps.remove(app);
    }

    @action
    clear() {
        return this.apps.clear();
    }

    @action
    setLocale() {
        return this.locale;
    }

    /**
     * Whether any app exists with given filters
     * @param  {Object} [filters={}]
     * @return {boolean}
     */
    existApps(filters = {}) {
        return this.apps.find(app => matchFilters(app, filters)) !== undefined;
    }

    /**
     * Get list of apps with given filters
     * @param  {Object} [filters={}]
     * @return {App[]}
     */
    filterApps(filters = {}) {
        return this.apps.filter(app => matchFilters(app, filters));
    }

    getLatestVersionProdforSapcode(sapcode, filters = {}) {
        const productsWithSapcode = this.filterApps({
            sapcode,
            ...filters,
        });
        const latestVersionModel = productsWithSapcode.reduce(
            (maxVersionApp, currVersionApp) => (versionComp(maxVersionApp.codexVersion,
                currVersionApp.codexVersion)
                ? currVersionApp : maxVersionApp), productsWithSapcode[0]
        );
        return latestVersionModel;
    }

    getProductCTAAction(productSapcode) {
        const productModel = this.getLatestVersionProdforSapcode(productSapcode);

        let sectionBasedField;

        if (productModel.isFullAppUpdate() || productModel.isPatchUpdate()) {
            sectionBasedField = productModel.getUpdateSectionBasedField();
        } else {
            sectionBasedField = productModel.getDefaultSectionBasedField();
        }


        if (sectionBasedField && sectionBasedField.buttonList
             && sectionBasedField.buttonList.length) {
            const { buttonList } = sectionBasedField;

            let requiredButon = buttonList[0];
            if (buttonList.length > 1) {
                requiredButon = buttonList
                    .reduce((buttonA,
                        buttonB) => ((PRODUCT_CTA_PRIORITY[buttonA.actionId]
                        > PRODUCT_CTA_PRIORITY[buttonB.actionId]) ? buttonA : buttonB));
            }

            const {
                iconType, localizedText, actionId, ariaLabel, actionType,
            } = requiredButon;

            if (actionId === ACTION_ID.RETRY) {
                if (productModel.errorAction === ACTION_ID.INSTALL) {
                    return {
                        actionId,
                        localizedText: TranslationUtil.getMessage('ccx.appAction.install'),
                    };
                } if (productModel.errorAction === ACTION_ID.UPDATE) {
                    return {
                        actionId,
                        localizedText: TranslationUtil.getMessage('ccx.appAction.update'),
                    };
                }
            }

            return {
                iconType, localizedText, actionId, ariaLabel, actionType,
            };
        }
        return null;
    }

    handleProductCTAClick(productSapcode, source, inAppSource) {
        const productModel = this.getLatestVersionProdforSapcode(productSapcode);
        const { actionId } = this.getProductCTAAction(productSapcode);
        productModel.onAppAction(actionId, productModel, {
            source,
            inAppSource,
        });
    }

    @computed
    get appVersionsBySAPCode() {
        const sapcodeVersionMap = {};
        this.apps.forEach((app) => {
            const { sapcode, codexVersion } = app;
            if (!(sapcode in sapcodeVersionMap)) {
                sapcodeVersionMap[sapcode] = [codexVersion];
            } else {
                const currentAppList = sapcodeVersionMap[sapcode];
                currentAppList.push(codexVersion);
                sapcodeVersionMap[sapcode] = currentAppList;
            }
        });
        return sapcodeVersionMap;
    }

    isMultipleVersionsAvailable(sapcode) {
        if (this.appVersionsBySAPCode[sapcode]) {
            if (this.appVersionsBySAPCode[sapcode].length > 1) { return true; }
        }
        return false;
    }
}

export default new Apps();
